import {
  View,
  FlatList,
  type ListRenderItemInfo,
  Animated,
  ImageBackground,
} from 'react-native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ValidatingDocumentsTakesCareSVG from '~/assets/svgs/checkoutSuccessTakesCareValidatingDocuments.svg';
import ValidatingDocumentsAutoSVG from '~/assets/svgs/checkoutSuccessAutoValidatingDocuments.svg';
import ValidatingDocumentsBagProtectionSVG from '~/assets/svgs/checkoutSuccessBagProtectionValidatingDocuments.svg';
import NextStepsTakesCareSVG from '~/assets/svgs/checkoutSuccessTakesCareNextSteps.svg';
import NextStepsProtectionSVG from '~/assets/svgs/checkoutSuccessAutoNextSteps.svg';
import InsuranceContractedTakesCareSVG from '~/assets/svgs/checkoutSuccessTakesCareInsuranceContracted.svg';
import InsuranceContractedProtectionSVG from '~/assets/svgs/checkoutSuccessAutoInsuranceContracted.svg';
import InsuranceContractedAutoSVG from '~/assets/svgs/checkoutSuccessAutoInsuranceContractedWeb.svg';
import type { CheckoutSuccessPage } from './types';
import Page from './components/Page';
import LastPageFooter from './components/LastPageFooter';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '~/navigation/types';
import { useNavigation } from '@react-navigation/native';
import { useCheckout, useSalesFunnel } from '~/stores';
import type { InsuranceType } from '~/@types/insurance';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { CheckoutStepNames } from '~/utils/salesFunnel';

export const SUCCESS_PAGE_WIDTH = 500;

const MARISA_TAKES_CARE_PAGES: CheckoutSuccessPage[] = [
  {
    id: 'documents',
    image: ValidatingDocumentsTakesCareSVG,
    text: 'Estamos confirmando as informações e enviaremos para seu email!',
  },
  {
    id: 'next_steps',
    image: NextStepsTakesCareSVG,
    text: 'Verifique sua caixa de email, inclusive a caixa de SPAM para ver a sua documentação e próximos passos!',
  },
  {
    id: 'contracted',
    image: InsuranceContractedTakesCareSVG,
    text: 'Parabéns! O Marisa Cuida foi contratado com sucesso!',
    footer: <LastPageFooter />,
  },
];

const MARISA_AUTO_PAGES: CheckoutSuccessPage[] = [
  {
    id: 'documents',
    image: ValidatingDocumentsAutoSVG,
    text: 'Estamos confirmando as informações e enviaremos para seu email!',
  },
  {
    id: 'next_steps',
    image: NextStepsProtectionSVG,
    text: 'Verifique sua caixa de email, inclusive a caixa de SPAM para ver a sua documentação e próximos passos!',
  },
  {
    id: 'contracted',
    image: InsuranceContractedAutoSVG,
    text: 'Parabéns!\n pela adesão do\n Seguro Auto Marisa',
    footer: <LastPageFooter />,
  },
];

const BAG_PROTECTION_PAGES: CheckoutSuccessPage[] = [
  {
    id: 'documents',
    image: ValidatingDocumentsBagProtectionSVG,
    text: 'Estamos confirmando as informações e enviaremos para seu email!',
  },
  {
    id: 'next_steps',
    image: NextStepsProtectionSVG,
    text: 'Verifique sua caixa de email, inclusive a caixa de SPAM para ver a sua documentação e próximos passos!',
  },
  {
    id: 'contracted',
    image: InsuranceContractedProtectionSVG,
    text: 'Parabéns! O Proteção Bolsa foi contratado com sucesso!',
    footer: <LastPageFooter />,
  },
];

const INCOME_PROTECTION_PAGES: CheckoutSuccessPage[] = [
  {
    id: 'documents',
    image: ValidatingDocumentsBagProtectionSVG,
    text: 'Estamos confirmando as informações e enviaremos para seu email!',
  },
  {
    id: 'next_steps',
    image: NextStepsProtectionSVG,
    text: 'Verifique sua caixa de email, inclusive a caixa de SPAM para ver a sua documentação e próximos passos!',
  },
  {
    id: 'contracted',
    image: InsuranceContractedProtectionSVG,
    text: 'Parabéns! O Proteção Perda de Renda foi contratado com sucesso!',
    footer: <LastPageFooter />,
  },
];

const getInsurancePages = (
  insuranceType: InsuranceType
): CheckoutSuccessPage[] => {
  if (insuranceType === 'marisa-auto') {
    return MARISA_AUTO_PAGES;
  }
  if (insuranceType === 'bag-protection') {
    return BAG_PROTECTION_PAGES;
  }
  if (insuranceType === 'income-protection') {
    return INCOME_PROTECTION_PAGES;
  }
  return MARISA_TAKES_CARE_PAGES;
};

const CheckoutSuccess = () => {
  const [showProgress, setShowProgress] = useState(true);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { newStepLog } = useSalesFunnel();

  const {
    params: { insuranceType },
  } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_CheckoutSuccess'>>();

  const listRef = useRef<FlatList>(null);
  const interval = useRef<NodeJS.Timeout>();
  const index = useRef<number>();
  const listScroll = useRef(new Animated.Value(0)).current;

  const { insurance } = useCheckout();

  const progress = listScroll.interpolate({
    inputRange: [0, SUCCESS_PAGE_WIDTH],
    outputRange: ['50%', '100%'],
    extrapolate: 'clamp',
  });

  const insurancePages = useMemo(
    () => getInsurancePages(insuranceType),
    [insuranceType]
  );

  const renderItem = useCallback(
    ({ item, index: pageIndex }: ListRenderItemInfo<CheckoutSuccessPage>) => (
      <Page {...item} isLastPage={pageIndex === insurancePages.length - 1} />
    ),
    [insurancePages]
  );

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (e) => {
        if (e.data.action.type === 'NAVIGATE') {
          return;
        }
        e.preventDefault();
      }),
    [navigation]
  );

  useEffect(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }

    interval.current = setInterval(() => {
      if ((index.current || 0) === insurancePages.length - 1) {
        clearInterval(interval.current);
        return;
      }

      const newIndex = (index.current || 0) + 1;

      listRef.current?.scrollToIndex({
        index: newIndex,
        animated: true,
      });

      index.current = newIndex;

      if (newIndex === insurancePages.length - 1) {
        setTimeout(() => setShowProgress(false), 150);
      }
    }, 2000);
  }, [insurancePages.length]);

  useEffect(() => {
    newStepLog(CheckoutStepNames.SUCCESS);
  }, [newStepLog]);

  return (
    <View className="bg-white h-[100vh] items-center justify-center flex-row">
      <ImageBackground
        source={insurance?.banner.web || {}}
        className="w-[518px] h-[669px] hidden lg:block"
      />

      <View className="w-[500px]">
        <Animated.FlatList
          ref={listRef}
          horizontal
          data={insurancePages}
          // pagingEnabled
          scrollEnabled={false}
          renderItem={renderItem}
          keyExtractor={({ id }) => id}
          showsHorizontalScrollIndicator={false}
          onScroll={Animated.event(
            [
              {
                nativeEvent: {
                  contentOffset: {
                    x: listScroll,
                  },
                },
              },
            ],
            { useNativeDriver: false }
          )}
        />

        {showProgress && (
          <View className="flex-1 items-center px-20 relative">
            <View className="h-1 w-[315px] bg-secondary-400 rounded absolute bottom-[150px]">
              <Animated.View
                className="h-[100%] bg-primary-600 rounded"
                style={{ width: progress }}
              />
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default CheckoutSuccess;
